/**
 * Blocks namespace
 * 
 * Provides a declarative API for creating BlockKit container blocks
 * such as sections, wizards, and lists.
 */

import { Elements } from './Elements';
import { BlockElement, BlockBuilder as BlockBuilderType } from '../BlockRenderer/types';

/**
 * Type guard to check if an object is a builder with a build method
 * @param obj - The object to check
 * @returns True if the object is a builder with a build method, false otherwise
 */
function isBuilder(obj: unknown): obj is BlockBuilderType {
  return obj !== null && 
         typeof obj === 'object' && 
         'build' in obj && 
         typeof (obj as { build: unknown }).build === 'function';
}

// Base interfaces for block properties
export interface SectionProps {
  heading?: BlockElement;
  text?: BlockElement;
  elements?: BlockElement[];
}

export interface WizardProps {
  elements: BlockElement[];
}

export interface WizardStepProps {
  title: string;
  elements: BlockElement[];
  nextButtonText?: string;
  acknowledgementText?: string;
}

export interface BulletListProps {
  items: BlockElement[];
}

export interface OrderedListProps {
  items: BlockElement[];
}

export interface StepListProps {
  steps: BlockElement[];
}

export interface BlockReplacementProps {
  ref: string;
  defaultContent?: BlockElement[];
}

// Base block builder class
abstract class BlockBuilder<T, P> {
  protected props: P;

  constructor(props?: Partial<P>) {
    this.props = props ? { ...props } as P : {} as P;
  }

  // Build method to convert to JSON
  abstract build(): T;

  // Clone method for immutability
  protected clone<B extends BlockBuilder<T, P>>(this: B): B {
    const Constructor = this.constructor as new (props: P) => B;
    return new Constructor({ ...this.props });
  }
}

// Section block
class SectionBlockBuilder extends BlockBuilder<Record<string, any>, SectionProps> {
  constructor(props?: Partial<SectionProps>) {
    super(props);
    this.props = {
      elements: [],
      ...this.props
    };
  }

  heading(heading: BlockBuilderType | { level?: 1 | 2 | 3 | 4 | 5 | 6, text: string }): this {
    const clone = this.clone();
    if (isBuilder(heading)) {
      clone.props.heading = heading.build();
    } else {
      clone.props.heading = Elements.Heading(heading).build();
    }
    return clone;
  }

  text(text: BlockBuilderType | { text: string, size?: 'small' | 'medium' | 'large' }): this {
    const clone = this.clone();
    if (isBuilder(text)) {
      clone.props.text = text.build();
    } else {
      clone.props.text = Elements.Text(text).build();
    }
    return clone;
  }

  addElement(element: BlockElement | BlockBuilderType): this {
    const clone = this.clone();
    const elementJson = isBuilder(element) ? element.build() : element;
    clone.props.elements = [...(clone.props.elements || []), elementJson];
    return clone;
  }

  /**
   * Adds multiple elements at once to the section
   * 
   * @param elements - Elements to add to the section
   */
  elements(...elements: (BlockElement | BlockBuilderType)[]): this {
    let result = this.clone();
    for (const element of elements) {
      result = result.addElement(element);
    }
    return result;
  }

  build(): Record<string, any> {
    const result: Record<string, any> = {
      type: 'section',
    };

    if (this.props.heading) {
      result.heading = this.props.heading;
    }

    if (this.props.text) {
      result.text = this.props.text;
    }

    if (this.props.elements && this.props.elements.length > 0) {
      result.elements = this.props.elements;
    }

    return result;
  }
}

// Wizard block
class WizardBlockBuilder extends BlockBuilder<Record<string, any>, WizardProps> {
  constructor(props?: Partial<WizardProps>) {
    super(props);
    this.props = {
      elements: [],
      ...this.props
    };
  }

  addStep(step: BlockElement | BlockBuilderType): this {
    const clone = this.clone();
    const stepJson = 'build' in step ? step.build() : step;
    clone.props.elements = [...clone.props.elements, stepJson];
    return clone;
  }
  
  /**
   * Adds multiple steps at once to the wizard
   * 
   * @param steps - Steps to add to the wizard
   */
  elements(...steps: (BlockElement | BlockBuilderType)[]): this {
    let result = this.clone();
    for (const step of steps) {
      result = result.addStep(step);
    }
    return result;
  }

  build(): Record<string, any> {
    if (!this.props.elements || this.props.elements.length === 0) {
      throw new Error('Wizard must have at least one step');
    }

    return {
      type: 'wizard',
      elements: this.props.elements
    };
  }
}

// Wizard step block
class WizardStepBlockBuilder extends BlockBuilder<Record<string, any>, WizardStepProps> {
  constructor(props?: Partial<WizardStepProps>) {
    super({
      title: '',
      elements: [],
      ...props
    });
  }

  title(title: string): this {
    const clone = this.clone();
    clone.props.title = title;
    return clone;
  }

  nextButtonText(text: string): this {
    const clone = this.clone();
    clone.props.nextButtonText = text;
    return clone;
  }

  acknowledgementText(text: string): this {
    const clone = this.clone();
    clone.props.acknowledgementText = text;
    return clone;
  }

  addElement(element: BlockElement | BlockBuilderType): this {
    const clone = this.clone();
    const elementJson = 'build' in element ? element.build() : element;
    clone.props.elements = [...(clone.props.elements || []), elementJson];
    return clone;
  }

  /**
   * Adds multiple elements at once to the wizard step
   * 
   * @param elements - Elements to add to the wizard step
   */
  elements(...elements: (BlockElement | BlockBuilderType)[]): this {
    let result = this.clone();
    for (const element of elements) {
      result = result.addElement(element);
    }
    return result;
  }

  build(): Record<string, any> {
    if (!this.props.title) {
      throw new Error('Wizard step must have a title');
    }

    if (!this.props.elements || this.props.elements.length === 0) {
      throw new Error('Wizard step must have at least one element');
    }

    const result: Record<string, any> = {
      type: 'wizard_step',
      title: this.props.title,
      elements: this.props.elements
    };

    if (this.props.nextButtonText) {
      result.nextButtonText = this.props.nextButtonText;
    }

    if (this.props.acknowledgementText) {
      result.acknowledgementText = this.props.acknowledgementText;
    }

    return result;
  }
}

// Bullet list block
class BulletListBlockBuilder extends BlockBuilder<Record<string, any>, BulletListProps> {
  constructor(props?: Partial<BulletListProps>) {
    super(props);
    this.props = {
      items: [],
      ...this.props
    };
  }

  addItem(item: BlockElement | BlockBuilderType): this {
    const clone = this.clone();
    const itemJson = 'build' in item ? item.build() : item;
    clone.props.items = [...clone.props.items, itemJson];
    return clone;
  }

  /**
   * Adds multiple items at once to the bullet list
   * 
   * @param items - Items to add to the bullet list
   */
  items(...items: (BlockElement | BlockBuilderType)[]): this {
    let result = this.clone();
    for (const item of items) {
      result = result.addItem(item);
    }
    return result;
  }

  build(): Record<string, any> {
    if (!this.props.items || this.props.items.length === 0) {
      throw new Error('Bullet list must have at least one item');
    }

    return {
      type: 'bullet_list',
      items: this.props.items
    };
  }
}

// Ordered list block
class OrderedListBlockBuilder extends BlockBuilder<Record<string, any>, OrderedListProps> {
  constructor(props?: Partial<OrderedListProps>) {
    super(props);
    this.props = {
      items: [],
      ...this.props
    };
  }

  addItem(item: BlockElement | BlockBuilderType): this {
    const clone = this.clone();
    const itemJson = 'build' in item ? item.build() : item;
    clone.props.items = [...clone.props.items, itemJson];
    return clone;
  }

  /**
   * Adds multiple items at once to the ordered list
   * 
   * @param items - Items to add to the ordered list
   */
  items(...items: (BlockElement | BlockBuilderType)[]): this {
    let result = this.clone();
    for (const item of items) {
      result = result.addItem(item);
    }
    return result;
  }

  build(): Record<string, any> {
    if (!this.props.items || this.props.items.length === 0) {
      throw new Error('Ordered list must have at least one item');
    }

    return {
      type: 'ordered_list',
      items: this.props.items
    };
  }
}

// Step list block
class StepListBlockBuilder extends BlockBuilder<Record<string, any>, StepListProps> {
  constructor(props?: Partial<StepListProps>) {
    super(props);
    this.props = {
      steps: [],
      ...this.props
    };
  }

  addStep(step: BlockElement | BlockBuilderType): this {
    const clone = this.clone();
    const stepJson = 'build' in step ? step.build() : step;
    clone.props.steps = [...clone.props.steps, stepJson];
    return clone;
  }
  
  /**
   * Adds multiple steps at once to the step list
   * 
   * @param steps - Steps to add to the step list
   */
  items(...steps: (BlockElement | BlockBuilderType)[]): this {
    let result = this.clone();
    for (const step of steps) {
      result = result.addStep(step);
    }
    return result;
  }

  build(): Record<string, any> {
    if (!this.props.steps || this.props.steps.length === 0) {
      throw new Error('Step list must have at least one step');
    }

    return {
      type: 'step_list',
      steps: this.props.steps
    };
  }
}

// Block replacement
class BlockReplacementBlockBuilder extends BlockBuilder<Record<string, any>, BlockReplacementProps> {
  constructor(props?: Partial<BlockReplacementProps>) {
    super(props);
    this.props = {
      ref: '',
      ...this.props
    };
  }

  ref(ref: string): this {
    const clone = this.clone();
    clone.props.ref = ref;
    return clone;
  }

  defaultContent(content: BlockElement[]): this {
    const clone = this.clone();
    clone.props.defaultContent = content.map(item => 'build' in item ? item.build() : item);
    return clone;
  }

  build(): Record<string, any> {
    if (!this.props.ref) {
      throw new Error('Block replacement must have a ref');
    }

    const result: Record<string, any> = {
      type: 'block_replacement',
      ref: this.props.ref
    };

    if (this.props.defaultContent) {
      result.defaultContent = this.props.defaultContent;
    }

    return result;
  }
}

// Main BlockBuilder class
class MainBlockBuilder extends BlockBuilder<Record<string, any>[], { blocks: Record<string, any>[] }> {
  constructor() {
    super({ blocks: [] });
  }

  blocks(...blocks: (Record<string, any> | { build(): Record<string, any> })[]): MainBlockBuilder {
    const clone = this.clone();
    const blockJsons = blocks.map(block => 'build' in block ? block.build() : block);
    clone.props.blocks = [...clone.props.blocks, ...blockJsons];
    return clone;
  }

  build(): Record<string, any>[] {
    return this.props.blocks;
  }

  /**
   * Converts the blocks to a JSON string
   * @param pretty - Whether to format the JSON with indentation (default: false)
   * @returns JSON string representation of the blocks
   */
  toJson(pretty = false): string {
    const blocks = this.build();
    return pretty ? JSON.stringify(blocks, null, 2) : JSON.stringify(blocks);
  }
}

// Blocks namespace factory functions
export const Blocks = {
  /**
   * Creates a MainBlockBuilder from a JSON string or object
   * @param json - JSON string or object representing blocks
   * @returns A new MainBlockBuilder instance with the parsed blocks
   */
  fromJson: (json: string | Record<string, any>[]): MainBlockBuilder => {
    const blocks = typeof json === 'string' ? JSON.parse(json) : json;
    return new MainBlockBuilder().blocks(...(Array.isArray(blocks) ? blocks : []));
  },
  builder: (): MainBlockBuilder => new MainBlockBuilder(),
  Section: (props?: Partial<SectionProps>): SectionBlockBuilder => new SectionBlockBuilder(props),
  Wizard: (props?: Partial<WizardProps>): WizardBlockBuilder => new WizardBlockBuilder(props),
  WizardStep: (props?: Partial<WizardStepProps>): WizardStepBlockBuilder => new WizardStepBlockBuilder(props),
  BulletList: (props?: Partial<BulletListProps>): BulletListBlockBuilder => new BulletListBlockBuilder(props),
  OrderedList: (props?: Partial<OrderedListProps>): OrderedListBlockBuilder => new OrderedListBlockBuilder(props),
  StepList: (props?: Partial<StepListProps>): StepListBlockBuilder => new StepListBlockBuilder(props),
  BlockReplacement: (props?: Partial<BlockReplacementProps>): BlockReplacementBlockBuilder => new BlockReplacementBlockBuilder(props)
};

export default Blocks;
